import React from 'react';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BloodworkAnalysis, PanelMarker, Section } from '../../models';
import { MARKER_BAR_PALETTE } from '../charts/chartPalette';


const isHdl = (pm: PanelMarker) => pm.attribute === 'hdl';

// Inflammation section: these markers only contribute to an inflammation root
// cause in one direction. The opposite direction has its own (non-inflammation)
// clinical meaning, so it shouldn't push the Inflammation button's color.
// Source: InflammationOxidativeStressAnalysis.calculateInsights.
const INFLAMMATION_HIGH_ONLY = new Set(['ceruloplasmin', 'ferritin', 'fibrinogen', 'ggt', 'hdl', 'hsCRP']);
// Uric acid flags both ways: HIGH → oxidative-stress / inflammatory (13175),
// LOW → 13170. So it is NOT direction-restricted here — both directions count
// toward the Inflammation section.
const INFLAMMATION_LOW_ONLY  = new Set(['albumin', 'bilirubin', 'tibc', 'transferrin']);

export const isInflammationIrrelevantDirection = (pm: PanelMarker): boolean => {
  if (!pm.hasValue()) return false;
  if (INFLAMMATION_HIGH_ONLY.has(pm.attribute) && !!pm.isStricklyLessThanOptimal?.()) return true;
  if (INFLAMMATION_LOW_ONLY.has(pm.attribute)  && !!pm.isGreaterThanOptimal?.())      return true;
  return false;
};

// Serum and RBC zinc carry an immune signal only when LOW — zinc is the direct
// measure of the deficiency that low Alk Phos only hints at. Optimal or high
// zinc says nothing about immune status. Attribute values match the field ids
// set in BloodworkAnalysis.ts (the four unit variants), split by sample type
// because RBC and serum zinc differ in how far a normal/high value is trusted.
const ZINC_RBC_ATTRIBUTES = new Set(['zincRBCμgdL', 'zincRBCmgL']);
const ZINC_SERUM_ATTRIBUTES = new Set(['zincSerumμgdL', 'zincSerummcgmL']);
const ZINC_ATTRIBUTES = new Set([
  'zincRBCμgdL', 'zincRBCmgL', 'zincSerumμgdL', 'zincSerummcgmL',
]);

// Whether a direct zinc value should overrule the low-Alk-Phos zinc proxy. RBC
// zinc tracks tissue status over the red-cell lifespan and isn't perturbed by
// recent meals or acute inflammation, so it supersedes ALP at ANY level. Serum
// zinc is homeostatically buffered with poor sensitivity — a normal/high value
// does not rule out functional deficiency — so it supersedes ALP only when it is
// LOW (low is its one reliable direction). Mirrors the CATEGORY_951 gate in
// BloodworkAnalysis.ts.
export const zincSupersedesAlp = (markers: PanelMarker[]): boolean =>
  markers.some(
    pm =>
      (ZINC_RBC_ATTRIBUTES.has(pm.attribute) && !!pm.hasValue?.()) ||
      (ZINC_SERUM_ATTRIBUTES.has(pm.attribute) &&
        !!pm.hasValue?.() &&
        !!pm.isStricklyLessThanOptimal?.()),
  );

// Drop Alk Phos from the Immune section when a direct zinc value supersedes it
// (see zincSupersedesAlp). `source` is the unfiltered WBC marker list —
// optimal/high zinc still lives there before keepInImmunePanel removes it —
// while `display` is the filtered list to prune.
export const suppressAlpWhenZincSupersedes = (
  display: PanelMarker[],
  source: PanelMarker[],
): PanelMarker[] =>
  zincSupersedesAlp(source)
    ? display.filter(pm => pm.attribute !== 'alkalinePhosphatase')
    : display;

// Immune section: ALP only maps to an immune root cause when LOW. High ALP
// reflects liver/biliary/bone turnover, not immune activity, so it shouldn't
// push the Immune button's color. Source: WBCAnalysis.calculateInsights.
export const isImmuneIrrelevantDirection = (pm: PanelMarker): boolean => {
  if (!pm.hasValue()) return false;
  if (pm.attribute === 'alkalinePhosphatase' && !!pm.isGreaterThanOptimal?.()) return true;
  // Zinc is an immune signal only when low; optimal/high zinc must not surface
  // in (or color) the Immune section.
  if (ZINC_ATTRIBUTES.has(pm.attribute) && !pm.isStricklyLessThanOptimal?.()) return true;
  return false;
};

// Globulin is an immune marker, but an in-optimal value contributes no signal
// to the immune analysis — hide its bar graph in that case.
export const isGlobulinOptimal = (pm: PanelMarker): boolean => {
  return pm.attribute === 'globulin' && !!pm.hasValue?.() && !!pm.isOptimal?.();
};

// "Native" markers live only in their section — no other section getter
// references them. Hiding them when optimal would mean the marker never
// appears anywhere, so the user couldn't see that it was tested. These
// markers always stay visible in their section, regardless of value.
const INFLAMMATION_NATIVE_ATTRIBUTES = new Set([
  'hsCRP', 'esr', 'homocysteine',
]);
// Absolute differential markers (Absolute Neutrophils, Lymphocytes, etc.).
// Their PanelMarker.attribute values use the full "*Absolute" form
// (see BloodworkAnalysis.ts), NOT the short "*Abs" field names.
const IMMUNE_ABSOLUTE_ATTRIBUTE_LIST = [
  'neutrophilsAbsolute', 'lymphocytesAbsolute', 'monocytesAbsolute',
  'eosinophilsAbsolute', 'basophilsAbsolute',
];
export const IMMUNE_ABSOLUTE_ATTRIBUTES = new Set(IMMUNE_ABSOLUTE_ATTRIBUTE_LIST);

export const isImmuneAbsolute = (pm: PanelMarker): boolean =>
  IMMUNE_ABSOLUTE_ATTRIBUTES.has(pm.attribute);

const IMMUNE_NATIVE_ATTRIBUTES = new Set([
  'whiteBloodCells', 'neutrophils', 'lymphocytes', 'monocytes', 'eosinophils', 'basophils',
  ...IMMUNE_ABSOLUTE_ATTRIBUTE_LIST,
  'nlr',
]);

const isOutOfOptimal = (pm: PanelMarker): boolean =>
  !!pm.isGreaterThanOptimal?.() || !!pm.isStricklyLessThanOptimal?.();

// "Keep in panel" predicates for the "Only show markers with a section
// signal" setting. A marker stays visible iff it is native to the section
// (no other home, so we must show it) OR it is out of optimal in a
// direction that maps to the section's clinical category.
export const keepInInflammationPanel = (pm: PanelMarker): boolean => {
  if (!pm.hasValue()) return false;
  if (INFLAMMATION_NATIVE_ATTRIBUTES.has(pm.attribute)) return true;
  if (!isOutOfOptimal(pm)) return false;
  return !isInflammationIrrelevantDirection(pm);
};

export const keepInImmunePanel = (pm: PanelMarker): boolean => {
  if (!pm.hasValue()) return false;
  if (IMMUNE_NATIVE_ATTRIBUTES.has(pm.attribute)) return true;
  if (!isOutOfOptimal(pm)) return false;
  return !isImmuneIrrelevantDirection(pm);
};

export const isHdlHighSoleOutOfOptimal = (markers: PanelMarker[]): boolean => {
  let foundHdlHigh = false;
  for (const pm of markers) {
    if (!pm.hasValue()) continue;
    const outOfOptimal = !!pm.isGreaterThanOptimal?.() || !!pm.isStricklyLessThanOptimal?.();
    if (!outOfOptimal) continue;
    if (isHdl(pm) && !!pm.isGreaterThanOptimal?.()) {
      foundHdlHigh = true;
      continue;
    }
    return false;
  }
  return foundHdlHigh;
};

export type Status = 'green' | 'yellow' | 'orange' | 'red';

export const SEVERITY: Record<Status, number> = { red: 0, orange: 1, yellow: 2, green: 3 };

// Lower SEVERITY = more concerning, so the worst of two statuses is the one
// with the lower severity score.
const worstStatus = (a: Status, b: Status): Status => (SEVERITY[a] <= SEVERITY[b] ? a : b);

// Toned-down per-status palette for the pill, tally badge, and row left accent.
// Softer than the old saturated fills (which read as harsh): a light tint `bg`
// with dark readable `text`, plus a medium `bar` for the accent/border. All
// 6-digit hex so they rasterize cleanly in the PDF.
const STATUS_SOFT: Record<Status, { bar: string; bg: string; text: string }> = {
  green:  { bar: '#5CB166', bg: '#DEF3E2', text: '#2A7134' },
  yellow: { bar: '#D8BE47', bg: '#FAF1C2', text: '#7A6912' },
  orange: { bar: '#E1974B', bg: '#FBE4C2', text: '#8A5616' },
  red:    { bar: '#E56A5A', bg: '#FAD6CE', text: '#B23322' },
};

// Human-readable status names shown as the pill on each section row and as the
// per-card Optimal/Monitor/Support/Priority tally. Single source of truth so the
// pill, the tally, and the legend can never drift out of sync.
export const STATUS_LABEL: Record<Status, string> = {
  green: 'Optimal',
  yellow: 'Monitor',
  orange: 'Support',
  red: 'Priority',
};

// Pale per-status row tint. Colors the whole card while keeping dark text
// readable; the deeper STATUS_SOFT palette stays reserved for the pill, tally
// badge, and left accent bar, so the stronger color still reads as the "signal".
const STATUS_TINT: Record<Status, string> = {
  green: '#E9FBEC',
  yellow: '#FDFCE0',
  orange: '#FFF1DC',
  red: '#FDE8E4',
};

// Surface for the system box. Stays white: a neutral grey was tried here to
// give the container a surface of its own, but against the pale status tints of
// the section boxes it read as grey-on-grey and cost more than it gained. The
// container is carried by its outline and top edge instead of by a fill.
const SYSTEM_BOX_SURFACE = '#fff';

// Segment colors for the per-section marker-distribution bar. Same hues as the
// rest of the report, but held a notch darker than the app-wide default: these
// segments are only 7px tall inside a busy card, and at the default alpha the
// green and yellow slivers washed out. See MARKER_BAR_PALETTE.
const MARKER_BAR_COLOR: Record<Status, string> = MARKER_BAR_PALETTE;

// Per-group identity color, mirroring the colored boxes in the Supplement
// Protocols and 7-Step Blueprint panels. Applied to each card's top accent bar,
// title, and header icon so the five systems each read as their own box — a
// separate color axis from the per-row status (pill + tint + left accent).
// Muted, earthy tones from the app's kit palette; kept solid + 6-digit hex so
// they render cleanly through html2canvas in the PDF.
const GROUP_COLOR: Record<string, string> = {
  'energize': '#2f6f7e',
  'build': '#3f7a55',
  'defend-repair': '#9c6a1e',
  'eliminate': '#6f4f89',
  'signal-regulate': '#3a6ea5',
};

// Blend a 6-digit hex toward white. Used to hold the group-color card outline
// well below the status-color row outlines nested inside it: the card frame and
// the row frames encode two unrelated things (which body system vs. how urgent),
// so drawing both as full-strength 1px rectangles makes them compete. Group
// identity is navigational and steps back; status is actionable and stays loud.
// Returns a solid hex rather than an alpha channel for the same reason the group
// colors themselves are 6-digit: html2canvas has to render it in the PDF.
const towardWhite = (hex: string, amount: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c =>
    Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0'),
  );
  return `#${channels.join('')}`;
};

// Tally display order: least → most concerning.
const STATUS_ORDER: Status[] = ['green', 'yellow', 'orange', 'red'];

// Soft rounded status pill at the right of each section row. Uses the toned-down
// STATUS_SOFT palette (light fill + dark text + medium border) so the label
// reads clearly without the harshness of the old saturated fills.
const pillStyle = (s: Status): React.CSSProperties => ({
  backgroundColor: STATUS_SOFT[s].bg,
  color: STATUS_SOFT[s].text,
  fontSize: '10px',
  fontWeight: 'bold',
  letterSpacing: '0.04em',
  padding: '2px 9px',
  borderRadius: 11,
  border: `1px solid ${STATUS_SOFT[s].bar}`,
  display: 'inline-block',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  lineHeight: 1.5,
});

// Circular badge for one status in the per-card tally. A zero count is muted
// gray so the eye lands on the statuses that actually occur in the card.
const countBadgeStyle = (s: Status, n: number, compact: boolean): React.CSSProperties => {
  const size = compact ? 18 : 22;
  return {
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: n > 0 ? STATUS_SOFT[s].bg : '#edeef0',
    color: n > 0 ? STATUS_SOFT[s].text : '#adb5bd',
    border: `1px solid ${n > 0 ? STATUS_SOFT[s].bar : '#e0e2e5'}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: compact ? 10 : 12,
    lineHeight: 1,
  };
};

const isHigherIsBetter = (pm: PanelMarker) => pm.isEGFR?.() || pm.field?.useEGFRChart;
const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;
const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
  const r = pm.range;
  if (!r) return 3;
  if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
  if (r.labMin > 0 && pm.value < r.labMin) return 2;
  return 3;
};

// Mirrors the band thresholds in LabsmartsGauge for the
// "Suboptimal Blood Oxygen Delivery" gauge, so the gauge's contribution to the
// Blood Oxygen card color matches the gauge color the practitioner sees.
export const anemicProbabilityToStatus = (probability?: number): Status => {
  if (probability === undefined || probability === null || probability < 0) return 'green';
  if (probability >= 90) return 'red';
  if (probability >= 70) return 'orange';
  if (probability >= 50) return 'yellow';
  return 'green';
};

// The Blood Oxygen card color is the more concerning of two signals:
//  - the "Suboptimal Blood Oxygen Delivery" gauge (anemicProbability), which
//    weighs LOW-side anemia combinations more accurately than naive marker
//    aggregation; and
//  - the marker-based status of the full RBC panel, which surfaces any marker
//    out of optimal in EITHER direction — notably HIGH RBC/HGB/HCT that the
//    anemia gauge ignores but that still warrant a look.
// getSectionStatus already treats RBC/HGB/HCT as two-sided and RDW as one-sided
// (isRdwChart), so it subsumes the previous RDW-only escalation add-on.
export const bloodOxygenStatus = (analysis: BloodworkAnalysis, markers: PanelMarker[]): Status => {
  const gauge = anemicProbabilityToStatus(analysis.anemicProbability);
  const markerStatus = getSectionStatus(markers, Section.RBC);
  return worstStatus(gauge, markerStatus);
};

// The Glucose card, like Blood Oxygen, is the more concerning of two signals:
//  - the "Glucose Regulation" gauge (glucoseAnalysis.glucoseRegulationProbability),
//    which weighs the combination of blood-sugar markers on the same 50/70/90
//    bands as the anemia gauge; and
//  - the marker-based status of the glucose panel (getSectionStatus, which keeps
//    the high-HDL-alone exception).
export const glucoseStatus = (analysis: BloodworkAnalysis, markers: PanelMarker[]): Status => {
  const gauge = anemicProbabilityToStatus(analysis.glucoseAnalysis?.glucoseRegulationProbability);
  const markerStatus = getSectionStatus(markers, Section.GLUCOSE);
  return worstStatus(gauge, markerStatus);
};

// The Immune card, like Blood Oxygen and Glucose, is the more concerning of two
// signals:
//  - the "Immune System Abnormality" gauge (wbcAnalysis.immuneSystemDisorderProbability),
//    graded on the same 50/70/90 bands (ticks [0,50,70,90] in the Immune Insights
//    panel) — it fires on WBC-differential / NLR pattern combinations even when
//    every individual marker sits inside its optimal range; and
//  - the marker-based status of the WBC panel (getSectionStatus, which already
//    drops immune-irrelevant directions like high ALP and non-low zinc).
export const immuneStatus = (analysis: BloodworkAnalysis, markers: PanelMarker[]): Status => {
  const gauge = anemicProbabilityToStatus(analysis.wbcAnalysis?.immuneSystemDisorderProbability);
  const markerStatus = getSectionStatus(markers, Section.WBC);
  return worstStatus(gauge, markerStatus);
};

// KNOWN LIMITATION — panel-composition confound. `worst` starts at green and
// only escalates on markers that have a value, so a returned `green` is
// ambiguous: it means EITHER every ordered marker was optimal OR the marker that
// would have flagged this section simply wasn't ordered on this test. Worst-wins
// over an uneven panel therefore lets a section's status move test-to-test from
// panel composition, not physiology (e.g. Stress & Adrenal reads green in a year
// where cortisol/DHEA-S weren't run). The pill itself still carries this
// ambiguity; the derived Improved/Worsened arrows, however, are guarded against
// it — see comparableStatusChange, which compares only the markers common to
// both tests.
export function getSectionStatus(markers: PanelMarker[], section?: Section): Status {
  // Glucose section exception: when high HDL is the ONLY out-of-optimal marker,
  // it more often reflects inflammation/autoimmunity than glucose dysregulation,
  // so don't color the section. (Low HDL still contributes; if any other marker
  // is also out of optimal, HDL contributes normally.)
  if (section === Section.GLUCOSE && isHdlHighSoleOutOfOptimal(markers)) {
    return 'green';
  }

  // Inflammation / Immune sections: a marker in its "non-section" direction
  // (e.g. high albumin for inflammation, high ALP for immune) reflects a
  // different clinical category, so exclude it from the section's color even
  // if other markers are also out.
  const effectiveMarkers =
    section === Section.INFLAMMATION_OXIDATIVE_STRESS
      ? markers.filter(pm => !isInflammationIrrelevantDirection(pm))
      : section === Section.WBC
        ? markers.filter(pm => !isImmuneIrrelevantDirection(pm))
        : markers;

  let worst: Status = 'green';
  for (const pm of effectiveMarkers) {
    if (!pm.hasValue()) continue;
    if (!isHigherIsBetter(pm) && pm.isGreaterThanAlarm?.()) return 'red';
    if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isLessThanAlarm?.()) return 'red';
    if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1) return 'red';
    if (!isHigherIsBetter(pm) && pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) { worst = 'orange'; continue; }
    if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.()) { worst = 'orange'; continue; }
    if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2) { worst = 'orange'; continue; }
    if (worst === 'green') {
      if (!isHigherIsBetter(pm) && pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) worst = 'yellow';
      else if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.()) worst = 'yellow';
      else if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3) worst = 'yellow';
    }
  }
  return worst;
}

// The single source of truth for a section's card color. RBC, Glucose, and
// Immune fold in their Insights gauge (worst of gauge vs markers — the gauge
// weighs combinations the naive marker aggregation misses); every other section
// is marker-only. Both the Results Overview panel and the AI export route through
// this so their colors can never drift.
export const sectionStatus = (
  analysis: BloodworkAnalysis,
  section: Section,
  markers: PanelMarker[],
): Status => {
  switch (section) {
    case Section.RBC: return bloodOxygenStatus(analysis, markers);
    case Section.GLUCOSE: return glucoseStatus(analysis, markers);
    case Section.WBC: return immuneStatus(analysis, markers);
    default: return getSectionStatus(markers, section);
  }
};

// Single-marker status band — the same escalation getSectionStatus applies, but
// per marker instead of taking the worst. Returns null for markers with no value
// or ones the section excludes (so the distribution bar counts only "relevant"
// markers, consistent with the section pill).
export function markerStatus(pm: PanelMarker, section?: Section): Status | null {
  if (!pm.hasValue()) return null;
  if (section === Section.INFLAMMATION_OXIDATIVE_STRESS && isInflammationIrrelevantDirection(pm)) return null;
  if (section === Section.WBC && (isGlobulinOptimal(pm) || isImmuneIrrelevantDirection(pm))) return null;
  if (!isHigherIsBetter(pm) && pm.isGreaterThanAlarm?.()) return 'red';
  if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isLessThanAlarm?.()) return 'red';
  if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1) return 'red';
  if (!isHigherIsBetter(pm) && pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) return 'orange';
  if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.()) return 'orange';
  if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2) return 'orange';
  if (!isHigherIsBetter(pm) && pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) return 'yellow';
  if (!isRdwChart(pm) && !isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.()) return 'yellow';
  if (!isRdwChart(pm) && isHigherIsBetter(pm) && pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3) return 'yellow';
  return 'green';
}

// Marker-level status distribution for a section's stacked bar. High HDL alone in
// the Glucose section is treated as non-concerning to stay consistent with the
// green pill (see isHdlHighSoleOutOfOptimal). Note: the RBC/Glucose gauge signals
// are NOT markers, so a section pill can be more concerning than these counts.
//
// Counts every marker passed in, so the total matches what the section actually
// renders. The Immune section's percentage and absolute differentials are two
// views of the same five cell populations, but they are two rendered markers and
// the count says so; hiding the absolutes is the "Show Absolutes" toggle's job,
// and callers drop them from `markers` before calling here when it is off.
export function sectionMarkerCounts(markers: PanelMarker[], section?: Section): { counts: Record<Status, number>; total: number } {
  const counts: Record<Status, number> = { green: 0, yellow: 0, orange: 0, red: 0 };
  const glucoseHdlSole = section === Section.GLUCOSE && isHdlHighSoleOutOfOptimal(markers);
  for (const pm of markers) {
    let st = markerStatus(pm, section);
    if (st === null) continue;
    if (glucoseHdlSole && isHdl(pm)) st = 'green';
    counts[st] += 1;
  }
  const total = counts.green + counts.yellow + counts.orange + counts.red;
  return { counts, total };
}

// The set of attributes that carry a value in a marker list.
const valuedAttributes = (markers: PanelMarker[]): Set<string> =>
  new Set(markers.filter(pm => pm.hasValue()).map(pm => pm.attribute));

// Direction of a section's status change between two tests, computed ONLY over
// the markers measured in BOTH tests. This defuses the panel-composition
// confound (see getSectionStatus): a marker newly ordered on the current test —
// or one dropped from it — isn't in the common set, so it can no longer make the
// section read as improved/worsened when nothing physiological changed. Returns
// 'improved' | 'worsened' | null (null when the two tests share no comparable
// marker, so there is nothing to judge the change by).
//
// Marker-based only: the RBC/Glucose gauges (bloodOxygenStatus / glucoseStatus)
// aren't recomputable on a marker subset, so they're excluded here. Callers AND
// this against the full-panel status direction (which DOES include the gauge), so
// an arrow shows only when the visible pill change is corroborated by comparable
// markers moving the SAME way — keeping the arrow consistent with the pill and
// never contradicting it.
export function comparableStatusChange(
  currentMarkers: PanelMarker[],
  prevMarkers: PanelMarker[],
  section?: Section,
): 'improved' | 'worsened' | null {
  const currentValued = valuedAttributes(currentMarkers);
  const prevValued = valuedAttributes(prevMarkers);
  // A marker counts only if its attribute carried a value in BOTH tests.
  const inBoth = (pm: PanelMarker): boolean =>
    pm.hasValue() && currentValued.has(pm.attribute) && prevValued.has(pm.attribute);
  const commonCurrent = currentMarkers.filter(inBoth);
  if (commonCurrent.length === 0) return null;
  const cur = getSectionStatus(commonCurrent, section);
  const prev = getSectionStatus(prevMarkers.filter(inBoth), section);
  if (SEVERITY[cur] > SEVERITY[prev]) return 'improved';
  if (SEVERITY[cur] < SEVERITY[prev]) return 'worsened';
  return null;
}

type SectionEntry = {
  section: Section;
  label: string;
  markers: PanelMarker[];
  show?: boolean;
  prevMarkers: PanelMarker[];
};

function buildSections(
  bloodworkAnalysis: BloodworkAnalysis,
  previousBloodworkAnalysis: BloodworkAnalysis | null | undefined,
  hasWbcValue: boolean,
): SectionEntry[] {
  const prev = previousBloodworkAnalysis ?? undefined;
  return [
    { section: Section.RBC,                           label: 'Blood Oxygen',        markers: bloodworkAnalysis.getRBCPanelMarkers(),                           prevMarkers: prev?.getRBCPanelMarkers() ?? [] },
    { section: Section.IRON,                          label: 'Iron Status',         markers: bloodworkAnalysis.getIronPanelMarkers(),                          prevMarkers: prev?.getIronPanelMarkers() ?? [] },
    { section: Section.WBC,                           label: 'Immune',              markers: bloodworkAnalysis.getWBCPanelMarkers(), show: hasWbcValue,         prevMarkers: prev?.getWBCPanelMarkers() ?? [] },
    { section: Section.PLATELETS,                     label: 'Blood Clotting',      markers: bloodworkAnalysis.getPlateletPanelMarkers(),                      prevMarkers: prev?.getPlateletPanelMarkers() ?? [] },
    { section: Section.GLUCOSE,                       label: 'Glucose',             markers: bloodworkAnalysis.getGlucosePanelMarkers(),                       prevMarkers: prev?.getGlucosePanelMarkers() ?? [] },
    { section: Section.KIDNEY,                        label: 'Kidney',              markers: bloodworkAnalysis.getKidneyPanelMarkers(),                        prevMarkers: prev?.getKidneyPanelMarkers() ?? [] },
    { section: Section.PROTEINS,                      label: 'Proteins',            markers: bloodworkAnalysis.getProteinsPanelMarkers(),                      prevMarkers: prev?.getProteinsPanelMarkers() ?? [] },
    { section: Section.NUTRIENTS,                     label: 'Vitamins & Minerals', markers: bloodworkAnalysis.getMineralPanelMarkers(),                       prevMarkers: prev?.getMineralPanelMarkers() ?? [] },
    { section: Section.ELECTROLYTES,                  label: 'Electrolytes',        markers: bloodworkAnalysis.getElectrolytePanelMarkers(),                   prevMarkers: prev?.getElectrolytePanelMarkers() ?? [] },
    { section: Section.LIVER_BILIARY,                 label: 'Liver',               markers: bloodworkAnalysis.getLiverPanelMarkers(),                         prevMarkers: prev?.getLiverPanelMarkers() ?? [] },
    { section: Section.LIPIDS,                        label: 'Lipids',              markers: bloodworkAnalysis.getLipidsPanelMarkers(),                        prevMarkers: prev?.getLipidsPanelMarkers() ?? [] },
    { section: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation',        markers: bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),   prevMarkers: prev?.getInflammationOxidativeStressPanelMarkers() ?? [] },
    { section: Section.THYROID,                       label: 'Thyroid',             markers: bloodworkAnalysis.getThyroidPanelMarkers(),                       prevMarkers: prev?.getThyroidPanelMarkers() ?? [] },
    { section: Section.STRESS_ADRENAL,                label: 'Stress & Adrenal',    markers: bloodworkAnalysis.getStressAdrenalPanelMarkers().filter(pm => pm.attribute !== 'sodium' && pm.attribute !== 'potassium'), prevMarkers: (prev?.getStressAdrenalPanelMarkers() ?? []).filter(pm => pm.attribute !== 'sodium' && pm.attribute !== 'potassium') },
    { section: Section.SEX_HORMONE,                   label: 'Hormones',            markers: bloodworkAnalysis.getSexHormonePanelMarkers(),                    prevMarkers: prev?.getSexHormonePanelMarkers() ?? [] },
    { section: Section.PROSTATE,                      label: 'Prostate',            markers: bloodworkAnalysis.getProstatePanelMarkers(),                      prevMarkers: prev?.getProstatePanelMarkers() ?? [] },
  ];
}

export type Group = { key: string; name: string; subtitle: string; sectionKeys: Section[]; tooltip: string[] };

// PDF only: pack the group cards into two independent vertical stacks instead
// of letting a wrapping flex row lay them out.
//
// A wrapping row aligns each new row to the bottom of the TALLEST card in the
// row above, so a short card (Digest & Build with two sections) left a tall
// empty hole beneath it while the card that should sit there (Detoxify &
// Eliminate) started way down the page. Stacking per column closes that hole:
// each card starts directly below the previous one in its own column.
//
// Cards alternate left/right so the on-screen reading order survives, and the
// odd one out (the 5th of five groups) goes to whichever column is shorter so
// the two stacks finish at roughly the same height rather than the left one
// running long. Section count stands in for card height — the per-card header
// is a constant, so the row count is what actually varies.
export const splitGroupsIntoColumns = <T extends { items: unknown[] }>(groups: T[]): T[][] => {
  const columns: T[][] = [[], []];
  const rows = [0, 0];
  groups.forEach((g, i) => {
    const isLast = i === groups.length - 1;
    const column = isLast ? (rows[1] < rows[0] ? 1 : 0) : i % 2;
    columns[column].push(g);
    rows[column] += g.items.length;
  });
  return columns.filter(c => c.length > 0);
};

export const GROUPS: Group[] = [
  {
    key: 'energize',
    name: 'Fuel & Energize',
    subtitle: 'Energy & Metabolic Function',
    sectionKeys: [Section.RBC, Section.IRON, Section.GLUCOSE, Section.LIPIDS, Section.THYROID],
    tooltip: [
      'Markers showing how the body delivers oxygen, mobilizes fuel, and paces metabolism.',
      'Covers oxygen transport (RBC, iron), fuel availability and handling (glucose, lipids), and metabolic rate control (thyroid).',
    ],
  },
  {
    key: 'build',
    name: 'Digest & Build',
    subtitle: 'Digestion, Nutrients, & Building Blocks',
    sectionKeys: [Section.NUTRIENTS, Section.PROTEINS],
    tooltip: [
      'Markers showing how well the body digests food, absorbs nutrients, and uses them as the building blocks for nearly every process in the body, including tissue, muscle, bones, enzymes, and immune function.',
      'Covers vitamin and mineral status (Nutrients) and protein status (Proteins). Low albumin is also in the Inflammation section.',
    ],
  },
  {
    key: 'defend-repair',
    name: 'Defend & Repair',
    subtitle: 'Immune, Inflammatory, & Repair Activity',
    sectionKeys: [Section.WBC, Section.INFLAMMATION_OXIDATIVE_STRESS, Section.PLATELETS],
    tooltip: [
      'Markers showing how the body is responding to immune challenge, inflammatory load, oxidative stress, and tissue repair demands.',
      'Covers systemic inflammation, innate and adaptive immune activity (WBC), and platelet-driven coagulation and repair. Mounting evidence frames this group as central to cardiovascular risk independent of lipid levels.',
    ],
  },
  {
    key: 'eliminate',
    name: 'Detoxify & Eliminate',
    subtitle: 'Detoxification & Elimination Function',
    sectionKeys: [Section.LIVER_BILIARY, Section.KIDNEY, Section.ELECTROLYTES],
    tooltip: [
      'Markers showing how the body filters waste, clears toxins, and maintains fluid and acid-base balance.',
      'Covers hepatic biotransformation (Liver), renal filtration (Kidney), and electrolyte and acid-base regulation (Electrolytes).',
    ],
  },
  {
    key: 'signal-regulate',
    name: 'Signal & Regulate',
    subtitle: 'Hormonal & Endocrine Health',
    sectionKeys: [Section.STRESS_ADRENAL, Section.SEX_HORMONE, Section.PROSTATE],
    tooltip: [
      'Markers showing how the body communicates and regulates reproductive, stress, and sex-specific physiology.',
      'Covers stress & adrenal balance (Na/K ratio, cortisol, DHEA-S, pregnenolone), sex hormones, and prostate.',
    ],
  },
];

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  previousBloodworkAnalysis?: BloodworkAnalysis | null;
  hasWbcValue: boolean;
  // 'row' wraps fixed-width cards horizontally (UI). 'column' stacks them
  // vertically (PDF in portrait orientation).
  direction?: 'row' | 'column';
  onSectionClick?: (section: Section) => void;
  // Mirror the section panel's "only show markers with a section signal"
  // filtering so the overview button hides when the underlying section
  // would render zero markers (and therefore hide itself).
  showOnlySectionSignals?: boolean;
  showOnlyImmuneSectionSignals?: boolean;
  // Mirrors the Immune section's "Show Absolutes" toggle. When off, the section
  // renders only the percentage differentials, so the overview's marker counts
  // must drop the absolutes too or the bar would tally markers the section is
  // no longer showing.
  showImmuneAbsolutes?: boolean;
}

// Order of the section rows inside a group card.
//
// Primary key is the status pill, worst-first. Because the pill is worst-wins,
// a single red marker makes a section Priority regardless of the rest of its
// panel — which left multi-Priority groups resolving on nothing but the order
// the sections happen to be listed in GROUPS.sectionKeys.
//
// So ties fall to the marker distribution the bar under each section name
// already shows: most Priority markers first, then Support, then Monitor. A
// section flagged by one red marker no longer outranks one flagged by a red
// plus eight oranges.
//
// These are raw counts, so a wide panel can outrank a narrow one on volume
// alone. That's intended (more flagged markers = more to review), but it does
// mean the order tracks panel size as well as severity — the same
// panel-composition caveat that applies to the pill itself.
//
// Sections tying on every count keep their GROUPS.sectionKeys order: Array#sort
// is stable, so the curated clinical order stays the last word.
type SortableSection = { status: Status; markerCounts: Record<Status, number> };

// Breadth: how many markers in a section sit outside optimal, at any level.
const outOfOptimalCount = (c: Record<Status, number>): number => c.yellow + c.orange + c.red;

const compareSectionSeverity = (a: SortableSection, b: SortableSection): number => {
  const byStatus = SEVERITY[a.status] - SEVERITY[b.status];
  if (byStatus !== 0) return byStatus;
  // Within a status tier, rank by breadth — total markers out of optimal —
  // rather than by depth at the worst level. A section with nine markers off
  // reads as the bigger problem than one with three in alarm range, even though
  // the second is deeper. Depth still decides ties, worst level first.
  const byBreadth = outOfOptimalCount(b.markerCounts) - outOfOptimalCount(a.markerCounts);
  if (byBreadth !== 0) return byBreadth;
  for (const s of ['red', 'orange', 'yellow'] as Status[]) {
    const byCount = b.markerCounts[s] - a.markerCounts[s];
    if (byCount !== 0) return byCount;
  }
  return 0;
};

// Compute the group cards the Results Overview panel would render. A group is
// dropped when none of its sections have renderable markers, so a test with no
// blood values entered yields an empty array — the signal callers use to hide
// the whole panel (see hasResultsOverviewContent).
export function buildResultsOverviewGroups(
  bloodworkAnalysis: BloodworkAnalysis,
  previousBloodworkAnalysis: BloodworkAnalysis | null | undefined,
  hasWbcValue: boolean,
  showOnlySectionSignals = true,
  showOnlyImmuneSectionSignals = true,
  showImmuneAbsolutes = true,
) {
  const sections = buildSections(bloodworkAnalysis, previousBloodworkAnalysis, hasWbcValue);
  const sectionBySection = new Map<Section, SectionEntry>(sections.map(s => [s.section, s]));

  // With "Show Absolutes" off, the Immune section hides its five absolute
  // differentials. They drop out of the overview with it — count, bar, status
  // pill, and the improved/worsened comparison alike — so a marker the
  // practitioner has chosen to hide can never drive what the overview shows.
  const shown = (section: Section, markers: PanelMarker[]): PanelMarker[] =>
    section === Section.WBC && !showImmuneAbsolutes
      ? markers.filter(pm => !isImmuneAbsolute(pm))
      : markers;

  const hasRenderableMarkers = (s: SectionEntry): boolean => {
    const markers = shown(s.section, s.markers);
    if (s.section === Section.INFLAMMATION_OXIDATIVE_STRESS && showOnlySectionSignals) {
      return markers.some(keepInInflammationPanel);
    }
    if (s.section === Section.WBC && showOnlyImmuneSectionSignals) {
      return markers.some(pm => !isGlobulinOptimal(pm) && keepInImmunePanel(pm));
    }
    return markers.some(pm => pm.hasValue());
  };

  return GROUPS
    .map(g => ({
      ...g,
      items: g.sectionKeys
        .map(k => sectionBySection.get(k))
        .filter((s): s is SectionEntry => !!s && s.show !== false && hasRenderableMarkers(s))
        .map(s => {
          // Marker distribution is computed here rather than at render because
          // the sort below tie-breaks on it; the row reuses the same numbers for
          // its marker bar. Both marker lists are the shown() ones, so the pill,
          // the bar, and the thumbs all read from exactly what is visible.
          const markers = shown(s.section, s.markers);
          const prevMarkers = shown(s.section, s.prevMarkers);
          const { counts, total } = sectionMarkerCounts(markers, s.section);
          return {
            ...s,
            markers,
            prevMarkers,
            status: sectionStatus(bloodworkAnalysis, s.section, markers),
            prevStatus: prevMarkers.length > 0 && previousBloodworkAnalysis
              ? sectionStatus(previousBloodworkAnalysis, s.section, prevMarkers)
              : null,
            markerCounts: counts,
            markerTotal: total,
          };
        })
        .sort(compareSectionSeverity),
    }))
    .filter(g => g.items.length > 0);
}

// True when the Results Overview panel has at least one group card to show.
// The panel (and its wrapping card header) should be hidden when this is false
// — e.g. a new test with no blood markers entered yet, which would otherwise
// render a title above an empty body.
export const hasResultsOverviewContent = (
  bloodworkAnalysis: BloodworkAnalysis,
  previousBloodworkAnalysis: BloodworkAnalysis | null | undefined,
  hasWbcValue: boolean,
  showOnlySectionSignals = true,
  showOnlyImmuneSectionSignals = true,
): boolean =>
  buildResultsOverviewGroups(
    bloodworkAnalysis,
    previousBloodworkAnalysis,
    hasWbcValue,
    showOnlySectionSignals,
    showOnlyImmuneSectionSignals,
  ).length > 0;

export const ResultsOverviewPanel: React.FC<Props> = ({
  bloodworkAnalysis,
  previousBloodworkAnalysis,
  hasWbcValue,
  direction = 'row',
  onSectionClick,
  showOnlySectionSignals = true,
  showOnlyImmuneSectionSignals = true,
  showImmuneAbsolutes = true,
}) => {
  const visibleGroups = buildResultsOverviewGroups(
    bloodworkAnalysis,
    previousBloodworkAnalysis,
    hasWbcValue,
    showOnlySectionSignals,
    showOnlyImmuneSectionSignals,
    showImmuneAbsolutes,
  );

  const isColumn = direction === 'column';
  // UI uses a fixed card width. PDF packs cards into two vertical stacks so all
  // group cards fit on a single portrait page (see splitGroupsIntoColumns).
  const cardWidth = isColumn ? '240px' : '255px';

  // "Top Areas to Review": the most concerning sections across every group —
  // only Support (orange) / Priority (red), ranked worst-first. Shows 3, or 5
  // when more than 3 sections are Priority.
  //
  // HIDDEN: the band works, but it made the panel too busy sitting above the
  // five system boxes. Flip to true to bring it back — the whole block below is
  // gated on it, and the legend paragraph describing it is commented out in
  // bloodwork-test-analysis.tsx (search "Top Areas to Review") to match.
  const showTopAreas = false;
  // Thin per-section marker-distribution bar under each section name. Set to
  // false to hide. Renders in the PDF as well as on screen: the PDF is meant to
  // be the same panel, so anything gated to one of the two reads as missing.
  const showMarkerBars = true;
  const rankedConcerns = visibleGroups
    .flatMap(g => g.items)
    .filter(it => it.status === 'red' || it.status === 'orange')
    .sort(compareSectionSeverity);
  // Widen the band to 5 when Priority sections alone overflow the default 3.
  // At four or more, a 3-slot band would cut Priority areas — the one thing it
  // exists to surface — so it grows rather than hiding them behind the cap.
  const priorityCount = rankedConcerns.filter(it => it.status === 'red').length;
  const topAreas = rankedConcerns.slice(0, priorityCount > 3 ? 5 : 3);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isColumn ? 12 : 16 }}>
      {showTopAreas && (
        <div
          style={{
            // Held a step stronger than the old #e6e9ef: the system boxes now
            // carry half-strength colored outlines, which left this band — the
            // most important row on the panel — with its faintest edge. Neutral
            // rather than colored, because the band cuts across all five groups
            // and any group hue would imply it belongs to one of them.
            border: '1px solid #d8dde3',
            borderRadius: 8,
            padding: '12px 14px',
            // White, mirroring a system box: the fill goes on the entries inside
            // it, the same way the section boxes carry the fill within a white
            // system box.
            backgroundColor: '#fff',
            // Shadow dropped in the PDF, matching the system boxes: html2canvas
            // tends to rasterize soft shadows as a grey smudge on white paper.
            ...(isColumn ? {} : { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)' }),
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8a9099', marginBottom: topAreas.length ? 8 : 0 }}>
            Top Areas to Review
          </div>
          {topAreas.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 10px', alignItems: 'center' }}>
              {topAreas.map((it, i) => {
                // Same panel-composition guard as the section rows below.
                const change = it.prevStatus !== null ? comparableStatusChange(it.markers, it.prevMarkers, it.section) : null;
                const improved = it.prevStatus !== null && SEVERITY[it.status] > SEVERITY[it.prevStatus] && change === 'improved';
                const worsened = it.prevStatus !== null && SEVERITY[it.status] < SEVERITY[it.prevStatus] && change === 'worsened';
                return (
                  <div
                    key={it.section}
                    onClick={onSectionClick ? () => onSectionClick(it.section) : undefined}
                    // Section box shape over a neutral fill, but both dialed back:
                    // every entry in this band is Priority or Support, so at full
                    // strength five identical outlines read as a wall of one color
                    // that discriminates nothing. The thin outline is halved and
                    // the fill sits just off white; the left edge stays full
                    // strength, which is the same split that settled the system
                    // boxes.
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      backgroundColor: '#fafbfc',
                      border: `1px solid ${towardWhite(STATUS_SOFT[it.status].bar, 0.5)}`,
                      borderLeft: `4px solid ${STATUS_SOFT[it.status].bar}`,
                      borderRadius: 6,
                      cursor: onSectionClick ? 'pointer' : 'default',
                    }}
                  >
                    <div style={countBadgeStyle(it.status, 1, false)}>{i + 1}</div>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#212529' }}>{it.label}</span>
                    <span style={pillStyle(it.status)}>{STATUS_LABEL[it.status]}</span>
                    {(improved || worsened) && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: worsened ? STATUS_SOFT.red.text : '#3f9a46' }}>
                        <FontAwesomeIcon icon={worsened ? 'thumbs-down' : 'thumbs-up'} style={{ fontSize: 11 }} />
                        {worsened ? 'Worsened' : 'Improved'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#6c757d' }}>
              No priority or support areas — all sections are Optimal or Monitor.
            </div>
          )}
        </div>
      )}
      {(() => {
      const renderGroupCard = (g: typeof visibleGroups[number]) => {
        const counts: Record<Status, number> = { green: 0, yellow: 0, orange: 0, red: 0 };
        g.items.forEach(it => { counts[it.status] += 1; });
        const gc = GROUP_COLOR[g.key] ?? '#6c757d';
        return (
        <div
          key={g.key}
          style={{
            flex: isColumn ? '0 0 auto' : `0 0 ${cardWidth}`,
            width: cardWidth,
            // Group color wraps the card as a thin outline so the container reads
            // as deliberately styled as the outlined section rows nested inside
            // it, but heavily lightened so it frames those rows instead of
            // competing with them. The top edge stays full strength: that single
            // saturated stroke is enough to carry group identity. Held at half
            // strength rather than lighter — with the section boxes tinted, a
            // fainter outline can't hold them and the groups stop reading.
            border: `1px solid ${towardWhite(gc, 0.5)}`,
            borderTop: `4px solid ${gc}`,
            borderRadius: isColumn ? '6px' : '8px',
            padding: isColumn ? '6px 10px 8px 10px' : '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: isColumn ? '4px' : '10px',
            // Recessed a step below the section boxes it contains. On white the
            // system box matched the page behind it, so it had no surface of its
            // own and the tinted section boxes read as loose items rather than
            // as five groups. Stepping it back gives the group an edge to hold.
            backgroundColor: SYSTEM_BOX_SURFACE,
            ...(isColumn ? {} : { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)' }),
          }}
        >
          <div
            style={{
              borderBottom: '1px solid #eef0f3',
              paddingBottom: isColumn ? '3px' : '10px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 6,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', fontSize: '15px', color: gc, lineHeight: isColumn ? 1.15 : 1.25, marginBottom: isColumn ? 4 : 2 }}>{g.name}</div>
              <div style={{ fontSize: isColumn ? '11px' : '12px', color: '#6c757d', lineHeight: isColumn ? 1.15 : 1.3 }}>{g.subtitle}</div>
            </div>
            {!isColumn && (
              <OverlayTrigger
                placement="top"
                trigger="click"
                rootClose
                overlay={
                  <Popover id={`results-overview-popover-${g.key}`} style={{ maxWidth: 340 }}>
                    <Popover.Content>
                      <div style={{ textAlign: 'left' }}>
                        {g.tooltip.map((para, i) => (
                          <div key={i} style={{ marginBottom: i === g.tooltip.length - 1 ? 0 : 8 }}>
                            {para}
                          </div>
                        ))}
                      </div>
                    </Popover.Content>
                  </Popover>
                }
              >
                <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer', color: gc, fontSize: 14 }} />
              </OverlayTrigger>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 4,
              paddingBottom: isColumn ? 2 : 6,
            }}
          >
            {STATUS_ORDER.map(s => (
              <div key={s} style={{ flex: 1, textAlign: 'center' }}>
                <div style={countBadgeStyle(s, counts[s], isColumn)}>{counts[s]}</div>
                <div style={{ fontSize: isColumn ? 8 : 10, color: '#6c757d', marginTop: 2, lineHeight: 1.1 }}>{STATUS_LABEL[s]}</div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'nowrap',
              gap: '7px',
            }}
          >
            {g.items.map(({ section, label, status, prevStatus, markers, prevMarkers, markerCounts, markerTotal }) => {
              // Panel-composition confound guard (see comparableStatusChange):
              // show an arrow only when the full-panel status change is
              // corroborated by the markers measured in BOTH tests moving the same
              // way. This suppresses false Improved/Worsened arrows driven purely
              // by a marker being newly ordered or dropped (e.g. cortisol/DHEA-S),
              // while keeping the arrow consistent with the displayed pill.
              const change = prevStatus !== null ? comparableStatusChange(markers, prevMarkers, section) : null;
              const improved = prevStatus !== null && SEVERITY[status] > SEVERITY[prevStatus] && change === 'improved';
              const worsened = prevStatus !== null && SEVERITY[status] < SEVERITY[prevStatus] && change === 'worsened';
              const showInflammationTooltip = section === Section.INFLAMMATION_OXIDATIVE_STRESS;
              const showImmuneTooltip = section === Section.WBC;
              const showBloodOxygenTooltip = section === Section.RBC;
              const showGlucoseTooltip = section === Section.GLUCOSE;
              const showStressAdrenalTooltip = section === Section.STRESS_ADRENAL;
              // The per-section explainer popovers are click affordances, so they
              // only apply on screen — column mode (PDF) strips all OverlayTriggers.
              const showSectionTooltip =
                !isColumn &&
                (showInflammationTooltip || showImmuneTooltip || showBloodOxygenTooltip || showGlucoseTooltip || showStressAdrenalTooltip);
              // Neutral row: the section name reads as plain text and the status is
              // carried by an explicit word (the pill) as well as its color, so the
              // meaning survives grayscale printing and color-blind viewing. A
              // near-white background also avoids the html2canvas "ghost rectangle"
              // the old solid-color buttons left in the PDF's second column.
              const accent = STATUS_SOFT[status].bar;
              const rowStyle: React.CSSProperties = {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                textAlign: 'left',
                padding: isColumn ? '4px 8px' : '8px 11px',
                backgroundColor: STATUS_TINT[status],
                // The status accent wraps the whole row as a thin outline, with
                // the left edge held thick so the color still reads as a bar.
                border: `1px solid ${accent}`,
                borderLeft: `4px solid ${accent}`,
                // Right corners squared off in the PDF ONLY to dodge an
                // html2canvas 1.0.0-rc.5 bug: BoundCurves insets the top-right
                // and bottom-right PADDING-box corners by `borderLeftWidth`
                // instead of `borderRightWidth` (see `topRightPaddingBox` /
                // `bottomRightPaddingBox` in html2canvas.js). With a rounded
                // corner and a 4px left accent that made the right border paint
                // as a 4px saturated wedge — the left bar mirrored onto the
                // right edge — instead of the 1px hairline the top and bottom
                // get. Zeroing the right-hand radii sends both corners down
                // html2canvas' square-corner path, which uses the correct
                // width. The rounded left edge (where the accent bar is) and
                // the on-screen 6px radius are untouched.
                borderRadius: isColumn ? '4px 0 0 4px' : 6,
                color: '#212529',
                boxShadow: 'none',
                ...(isColumn ? { outline: 'none', transition: 'none' } : {}),
              };
              const tooltipBody = !showSectionTooltip
                ? null
                : showInflammationTooltip
                ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      Inflammation root causes only apply for specific directions of each marker:
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>Only HIGH counts:</strong> ceruloplasmin, ferritin, fibrinogen, GGT, HDL, hsCRP
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>Only LOW counts:</strong> albumin, bilirubin, TIBC, transferrin
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Either direction:</strong> homocysteine, ESR, uric acid
                    </div>
                    <div>
                      Values out of range in the other direction reflect a different clinical category and don't affect this section's color.
                    </div>
                  </>
                )
                : showImmuneTooltip
                ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      This section's status is the more concerning of two signals:
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>Immune System Abnormality gauge:</strong> the likelihood from the combined WBC-differential and NLR patterns — it can raise the section even when each marker sits inside its optimal range.
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Out-of-optimal markers:</strong> these immune markers (WBC, neutrophils, lymphocytes, monocytes, eosinophils, basophils, NLR, globulin) count in <strong>both directions</strong>.
                    </div>
                    <div>
                      <strong>Alkaline phosphatase</strong> only signals immune activity when <strong>LOW</strong>. High ALP reflects liver, biliary, or bone turnover and doesn't affect this section's color.
                    </div>
                  </>
                )
                : showStressAdrenalTooltip
                ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      These stress &amp; adrenal markers (Na/K ratio, cortisol AM, cortisol PM, DHEA-S, pregnenolone) count in <strong>both directions</strong>.
                    </div>
                    <div>
                      <strong>Sodium</strong> and <strong>potassium</strong> on their own don't affect this section's color. When the Na/K ratio is optimal but sodium or potassium is out of optimal, they're counted in the <strong>Electrolytes</strong> section instead.
                    </div>
                  </>
                )
                : showGlucoseTooltip
                ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      This section's status is the more concerning of two signals:
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>Glucose Regulation gauge:</strong> the blood-sugar regulation likelihood, weighing markers such as glucose, HbA1c, triglycerides, and insulin or C-peptide together.
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Out-of-optimal markers:</strong> any glucose-panel marker outside its optimal range.
                    </div>
                    <div>
                      A high HDL on its own doesn't flag this section — it more often reflects inflammation than blood-sugar regulation. An Optimal status means neither signal is raised.
                    </div>
                  </>
                )
                : (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      This section's status is the more concerning of two signals:
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>Anemia gauge:</strong> the Suboptimal Blood Oxygen Delivery likelihood, driven by <strong>low</strong> RBC, hemoglobin, and hematocrit.
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Out-of-optimal markers:</strong> any RBC-panel marker outside its optimal range in <strong>either direction</strong> — so high RBC, hemoglobin, or hematocrit still flag the section even when anemia is Less Likely.
                    </div>
                    <div>
                      An Optimal status means neither signal is raised.
                    </div>
                  </>
                );
              const tooltipId = showInflammationTooltip
                ? 'inflammation-markers-tooltip'
                : showImmuneTooltip
                ? 'immune-markers-tooltip'
                : showStressAdrenalTooltip
                ? 'stress-adrenal-markers-tooltip'
                : showGlucoseTooltip
                ? 'glucose-markers-tooltip'
                : 'blood-oxygen-markers-tooltip';
              return (
                <Button
                  key={section}
                  variant="light"
                  size="sm"
                  style={rowStyle}
                  onClick={onSectionClick ? () => onSectionClick(section) : undefined}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, lineHeight: 1.15, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{label}</span>
                      {showSectionTooltip && (
                        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                          <OverlayTrigger
                            placement="top"
                            trigger="click"
                            rootClose
                            overlay={
                              <Popover id={tooltipId} style={{ maxWidth: 340 }}>
                                <Popover.Content>
                                  <div style={{ textAlign: 'left' }}>{tooltipBody}</div>
                                </Popover.Content>
                              </Popover>
                            }
                          >
                            <FontAwesomeIcon
                              icon="info-circle"
                              style={{ cursor: 'pointer', color: '#6c757d', fontSize: 11, flexShrink: 0 }}
                            />
                          </OverlayTrigger>
                        </span>
                      )}
                    </span>
                    {showMarkerBars && markerTotal > 0 && (
                      <span
                        title={STATUS_ORDER.filter(s => markerCounts[s] > 0).map(s => `${markerCounts[s]} ${STATUS_LABEL[s]}`).join('  ·  ')}
                        style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ display: 'flex', gap: 1, width: 92, height: 7, borderRadius: 3, overflow: 'hidden', backgroundColor: '#fff', border: '1px solid #e3e5e8' }}>
                            {STATUS_ORDER.map(s => (markerCounts[s] > 0 ? (
                              <span key={s} style={{ flexGrow: markerCounts[s], flexBasis: 0, backgroundColor: MARKER_BAR_COLOR[s] }} />
                            ) : null))}
                          </span>
                          <span style={{ fontSize: 10, color: '#8a9099', fontWeight: 600 }}>{markerTotal}</span>
                        </span>
                        {/* Per-status counts, previously hover-only — a title
                            attribute is unreachable on touch, and a bare segment bar
                            carries proportion but not scale. Each number sits under
                            the segment it belongs to: this row mirrors the bar's
                            flexGrow weights and its 1px border inset (as padding) so
                            the two stay in register, and minWidth:0 lets a thin
                            slice's numeral overflow its slot rather than push the
                            row out of alignment. */}
                        <span style={{ display: 'flex', gap: 1, width: 92, padding: '0 1px' }}>
                          {STATUS_ORDER.map(s => (markerCounts[s] > 0 ? (
                            <span
                              key={s}
                              title={`${markerCounts[s]} ${STATUS_LABEL[s]}`}
                              style={{
                                flexGrow: markerCounts[s],
                                flexBasis: 0,
                                minWidth: 0,
                                textAlign: 'center',
                                fontSize: 10,
                                lineHeight: 1.1,
                                fontWeight: 700,
                                color: STATUS_SOFT[s].text,
                              }}
                            >
                              {markerCounts[s]}
                            </span>
                          ) : null))}
                        </span>
                      </span>
                    )}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {improved && (
                      <FontAwesomeIcon icon="thumbs-up" title="Improved" style={{ fontSize: 12, color: '#3f9a46' }} />
                    )}
                    {worsened && (
                      <FontAwesomeIcon icon="thumbs-down" title="Worsened" style={{ fontSize: 12, color: '#d34a3c' }} />
                    )}
                    <span style={pillStyle(status)}>{STATUS_LABEL[status]}</span>
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
        );
      };

      // PDF: two masonry-style stacks (see splitGroupsIntoColumns) so no card
      // waits on a taller neighbour before it can start. UI: one wrapping row
      // of fixed-width cards, which reflows with the browser window.
      if (isColumn) {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 50, alignItems: 'flex-start', justifyContent: 'center' }}>
            {splitGroupsIntoColumns(visibleGroups).map((column, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 44 }}>
                {column.map(renderGroupCard)}
              </div>
            ))}
          </div>
        );
      }
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
        >
          {visibleGroups.map(renderGroupCard)}
        </div>
      );
      })()}
    </div>
  );
};
